import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ModalShell from "../Templates/ModalShell";
import RuleBuilder from "./RuleBuilder";
import Spinner from "../../components/Spinner/Spinner";
import {
  deleteMaterialAdaptation,
  getMaterialAdaptation,
  putMaterialAdaptation,
} from "../../services/materialsService";
import { formatApiError } from "../../utils/helpers/apiErrors";

// Per-school recommendation editor for ONE material. The caller edits only their
// OWN school's overlay (the backend resolves the target org server-side), so the
// RuleBuilder is always fed the caller's single active rubric.
//
// INVARIANT (no "= ?"): the RuleBuilder only ever receives rules that belong to
// the loaded rubric (`rubric.uuid`). Overlay rules authored for a PREVIOUS rubric
// version are split out as read-only "stale" chips here and never handed to the
// editor — so a question/answer dropdown can never fail to resolve.
const matchesRubric = (rule, rubric) =>
  !rule.template_version || rule.template_version === rubric?.uuid;

// Overlay rules come back as { question, operator, expected_option, ... }; the
// editor + PUT body only need these three.
const toEditable = (rule) => ({
  question: rule.question,
  operator: rule.operator,
  expected_option: rule.expected_option,
});

export default function AdaptationModal({ material, rubric, onClose, onSaved }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [state, setState] = useState(null); // raw /adaptation/ payload
  const [rules, setRules] = useState([]); // editable (current-rubric) rules
  const [staleRules, setStaleRules] = useState([]); // read-only, prior rubric

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    getMaterialAdaptation({ uuid: material.uuid, signal: controller.signal })
      .then((data) => {
        setState(data);
        const own = data.rules || [];
        setRules(own.filter((r) => matchesRubric(r, rubric)).map(toEditable));
        setStaleRules(own.filter((r) => !matchesRubric(r, rubric)));
        setLoadError("");
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setLoadError(formatApiError(err, t("materials.adaptationLoadFailed")));
      })
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, [material.uuid, rubric, t]);

  // The owner defaults can only seed a child's overlay when they're authored in
  // the SAME rubric this school runs (else there's nothing comparable to clone).
  const seedableDefaults = useMemo(
    () => (state?.default_rules || []).filter((r) => matchesRubric(r, rubric)),
    [state, rubric],
  );
  const canSeedFromDefaults =
    state?.inheriting_default && rules.length === 0 && seedableDefaults.length > 0;

  const startFromDefaults = () => setRules(seedableDefaults.map(toEditable));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Only current-rubric rules are sent; stale rules are intentionally dropped.
      await putMaterialAdaptation({
        uuid: material.uuid,
        rules: rules.map((r) => ({
          question: r.question,
          operator: r.operator,
          expected_option: r.expected_option,
        })),
      });
      toast.success(t("materials.adaptationSaved"));
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err, t("materials.adaptationSaveFailed")));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevert = async () => {
    setIsSaving(true);
    try {
      await deleteMaterialAdaptation({ uuid: material.uuid });
      toast.success(t("materials.adaptationReverted"));
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err, t("materials.adaptationSaveFailed")));
    } finally {
      setIsSaving(false);
    }
  };

  const hasOwnOverlay = state && !state.inheriting_default;

  return (
    <ModalShell onClose={onClose} busy={isSaving} maxWidth="max-w-2xl">
      <h3 className="text-lg font-bold text-slate-900 pr-8 mb-1">
        {t("materials.recommendations")}
      </h3>
      <p className="text-xs text-slate-500 mb-4 truncate">{material.title}</p>

      {isLoading ? (
        <div className="py-12">
          <Spinner label={t("common.loading")} />
        </div>
      ) : loadError ? (
        <p className="py-8 text-sm text-rose-600">{loadError}</p>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            {t("materials.adaptationHint")}
          </p>

          {state?.inheriting_default && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
              {t("materials.inheritingDefault")}
              {canSeedFromDefaults && (
                <button
                  type="button"
                  onClick={startFromDefaults}
                  className="ml-2 font-bold text-indigo-600 hover:text-indigo-700"
                >
                  {t("materials.startFromDefaults")}
                </button>
              )}
            </div>
          )}

          {staleRules.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
              <p className="text-[11px] font-bold text-amber-700 mb-1.5">
                {t("materials.staleRulesNote")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {staleRules.map((r) => (
                  <span
                    key={r.uuid}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-amber-200 text-[11px] font-semibold text-slate-600"
                  >
                    {r.question_text || r.question}
                    <button
                      type="button"
                      onClick={() =>
                        setStaleRules((prev) => prev.filter((x) => x.uuid !== r.uuid))
                      }
                      className="text-amber-500 hover:text-rose-600"
                      aria-label={t("materials.removeRule")}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <RuleBuilder
            rubric={rubric}
            value={rules}
            onChange={setRules}
            disabled={isSaving}
          />

          <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
            <div>
              {hasOwnOverlay && (
                <button
                  type="button"
                  onClick={handleRevert}
                  disabled={isSaving}
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60"
                >
                  {t("materials.revertToDefault")}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {t("materials.saveRecommendations")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
